import { Component, inject } from '@angular/core';
import { Tag } from '@models/common/tag';
import { Company } from "@models/companies/company";
import { Entity } from '@models/companies/entity';
import { Individual } from "@models/individuals/individual";
import { Trust } from "@models/trusts/trust";
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import {
  getTagEntityId,
  getTagEntityTypeEnum,
  ITagsEntity,
  TagEntityTypeEnum
} from "@pages/settings/components/manage-tags-page/manage-tags-modal/manage-tags-modal.constant";
import { TagComponent } from '@shared/components/commons/tag/tag.component';
import {
  SelectTagsDropdownComponent
} from '@shared/components/entity-profiles/entity-profile-tags/select-tags-dropdown/select-tags-dropdown.component';
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { TagsService } from "@shared/services/tags.service";
import { doNothing } from "@shared/utils/doNothing";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from 'ag-grid-community';
import { catchError, of, tap } from 'rxjs';

type CellRendererParams = ICellRendererParams<ITagsEntity> & {
  allowEdit?: boolean;
  wrappable?: boolean;
  useValueForTags?: boolean;
  handleAddNewTag?: (() => void)
  onClickTag?: ((tag: Tag) => void)
}

@Component({
  selector: 'app-ag-entity-tags',
  standalone: true,
  imports: [
    TagComponent,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    SelectTagsDropdownComponent,
    HasRoleDirective
  ],
  templateUrl: './ag-entity-tags.component.html',
  styleUrl: './ag-entity-tags.component.scss'
})
export class AgEntityTagsComponent implements ICellRendererAngularComp {
  tagsService = inject(TagsService);

  params!: CellRendererParams;

  entity!: ITagsEntity;
  systemTags: Tag[] = [];
  tags: Tag[] = [];
  wrappable = false;
  allowEdit = false;
  useValueForTags = false;

  agInit(params: CellRendererParams): void {
    this.setParams(params);
  }

  refresh(params: CellRendererParams): boolean {
    this.setParams(params);
    return true;
  }

  setParams(params: CellRendererParams): void {
    this.params = params;
    this.entity = params.data as Entity & Individual & Company & Trust;
    this.allowEdit = params.allowEdit ?? false;
    this.wrappable = params.wrappable ?? false;
    this.useValueForTags = params.useValueForTags ?? false;
    this.systemTags = (params.data && 'systemTags' in params.data ? (params.data?.systemTags ?? []) : []).map(tag => new Tag(tag)) || [];
    this.tags = ((this.useValueForTags ? params.value : params.data?.tags) as Tag[] ?? [])?.map((tag: Tag) => new Tag(tag));
    this.tags?.sort((a, b) => a.name.localeCompare(b.name));
  }

  assignTag({ tag, assign }: { tag: Tag, assign: boolean }): void {
    if (assign && !this.tags.find(t => t.tagId === tag.tagId)) {
      this.tags = [...this.tags, tag];
      this.entity.tags = [...this.entity.tags, tag];
    } else {
      this.tags = this.tags.filter(t => t.tagId !== tag.tagId);
      this.entity.tags = this.entity.tags.filter(t => t.tagId !== tag.tagId);
    }

    this.tagsService.assignTag(tag, this.entityId, assign, this.entityType)
      .pipe(catchError((error) => {
        console.warn('[AgEntityTagsComponent] assignTag: ', error);
        return of();
      }))
      .subscribe();
  }

  addNewTag(): void {
    const { modalRef, instance } = this.tagsService.openCreateEditTagModal();
    instance.isEdit = false;
    modalRef.result.then((tag: Tag) => {
        this.tagsService.createOrUpdateTag(tag, this.entityId, true, this.entityType)
          .pipe(
            tap((tagId) => {
              this.assignTag({ tag: new Tag({ ...tag, tagId }), assign: true });
            }),
            catchError((error) => {
              console.warn('[AgEntityTagsComponent] createOrUpdateTag: ', error);
              return of([]);
            }),
          )
          .subscribe({ next: () => this.params.handleAddNewTag?.() });
      },
      doNothing
    );
  }

  clickTag(tag: Tag): void {
    this.params.onClickTag?.(tag);
  }

  get entityId(): string {
    return getTagEntityId(this.entity);
  }

  get entityType(): TagEntityTypeEnum {
    return getTagEntityTypeEnum(this.entity);
  }
}
